import {
  IconButton,
  IconColor,
  IconName,
  IconSize,
  TipPlacement,
  Tooltip
} from '@focaldata/cin-ui-components'
import { FC } from 'react'
import useStyles from './MarketVisibilityIndicatorIcon.styles'
import {
  formatMarketLine,
  useHiddenMarketsLabel
} from './useHiddenMarketsLabel'

// The tooltip lists this many markets, then collapses the tail to "+k more" so it
// stays a fixed, scannable height regardless of how many markets a project has.
const MAX_TOOLTIP_MARKETS = 8

interface Props {
  hiddenInMarkets: string[]
  parentHiddenInMarkets?: string[]
  tipPlacement?: TipPlacement
  /**
   * Where it sits: 'header' (question-card toolbar, aligned to the delete button)
   * or 'adornment' (a response-option / matrix row's end-adornment strip).
   */
  variant?: 'header' | 'adornment'
  onEdit: () => void
}

/**
 * Variant A. A globe that appears only when the item is hidden in some markets;
 * the full (capped) list of hidden markets shows on hover, and clicking opens the
 * market-visibility dialog. Rendered as an IconButton so it matches the other
 * header icons (size, colour, hover, keyboard/aria). Reused on the question-card
 * header and — once the design-system slot lands — on response-option / matrix rows.
 */
const MarketVisibilityIndicatorIcon: FC<Props> = ({
  hiddenInMarkets,
  parentHiddenInMarkets,
  tipPlacement = TipPlacement.Top,
  variant = 'header',
  onEdit
}) => {
  const { classes } = useStyles()
  const { isHidden, hiddenMarkets, hiddenCount, totalMarkets } =
    useHiddenMarketsLabel(hiddenInMarkets, parentHiddenInMarkets)

  if (!isHidden) {
    return null
  }

  const heading = `Hidden in ${hiddenCount} of ${totalMarkets} markets`
  const shown = hiddenMarkets.slice(0, MAX_TOOLTIP_MARKETS)
  const remainder = hiddenCount - shown.length
  // Full, uncapped list for screen readers.
  const ariaLabel = `${heading}: ${hiddenMarkets
    .map((market) => market.label)
    .join(', ')}`

  // Plain spans (no <Text>) so the content inherits the tooltip's own light text
  // colour/font, staying consistent with every other tooltip in the app.
  const tooltip = (
    <span className={classes.tooltip}>
      <span className={classes.tooltipHeading}>{heading}</span>
      {shown.map((market) => (
        <span key={market.surveyId}>{formatMarketLine(market)}</span>
      ))}
      {remainder > 0 && <span>{`+${remainder} more`}</span>}
    </span>
  )

  return (
    <Tooltip title={tooltip} tipPlacement={tipPlacement}>
      <span
        className={
          variant === 'adornment'
            ? classes.triggerAdornment
            : classes.triggerHeader
        }
      >
        <IconButton
          iconName={IconName.Public}
          iconSize={IconSize.Large}
          iconColor={IconColor.Background}
          ariaLabel={ariaLabel}
          onClick={onEdit}
          negativePadding
          smallHoverShadowSize
        />
      </span>
    </Tooltip>
  )
}

export default MarketVisibilityIndicatorIcon
